import get from 'lodash/get.js'
import {createSelector} from 'reselect'
import classNames from 'classnames'

import {isPresent, isNumeric, isPositiveNumeric} from '../../../common/utils.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import NumberInput from '../../../common/components/Form/NumberInput.js'
import TextInput from '../../../common/components/TextInput.js'
import {
  getProduct,
  saveProductWarehouse,
  productWarehousesSelector,
  canEditTotalOnHandSelector,
} from '../../../data/products.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {
  warehouseSelector,
  getWarehouseName,
  warehouseOptionsSelector,
} from '../../../data/warehouses.js'
import {usesInventorySelector} from '../../../data/company.js'
import {formsSelector} from '../../../redux/selectors/ui/forms.js'
import TextArea from '../../../common/components/TextArea.js'
import {
  setForm,
  updateForm,
  removeForm,
  onlyIfForm,
  useSelector,
  getState,
} from '../../../store.js'
import Zelect from '../../../common/components/Zelect.js'
import {useMemo} from 'react'

export const MODAL_FORM = 'edit_product_warehouse_modal'

export function showEditProductWarehouseModal(
  sku,
  warehouseIDs = [],
  autoFocusOn = null,
) {
  const productWarehouses = productWarehousesSelector(getState(), {sku})
  const totalThatCanEditTotalOnHand = warehouseIDs
    .map((warehouseID) =>
      canEditTotalOnHandSelector(getState(), {sku, warehouseID}),
    )
    .filter((canEditTotalOnHand) => canEditTotalOnHand).length

  const unassignedWarehouseOptions = warehouseOptionsSelector(
    getState(),
  ).filter(({entity}) => !productWarehouses.find(({id}) => entity.id === id))

  autoFocusOn = autoFocusOn || 'location_in_warehouse'

  const firstProductWarehouse = productWarehouses.find(
    ({id}) => id === warehouseIDs[0],
  )
  const low_stock_threshold = get(firstProductWarehouse, 'low_stock_threshold')

  setForm(MODAL_FORM, {
    sku,
    warehouseIDs,
    newWarehouseID:
      (warehouseIDs.length === 0 && unassignedWarehouseOptions[0]?.entity.id) ||
      null,
    autoFocusOn,
    totalThatCanEditTotalOnHand,
    isSingleInputEdit: warehouseIDs.length > 1,
    location_in_warehouse:
      get(firstProductWarehouse, 'location_in_warehouse') || '',
    physical_on_hand: String(
      get(firstProductWarehouse, 'physical_on_hand') || 0,
    ),
    adjustment: '',
    low_stock_threshold: String(
      typeof low_stock_threshold === 'number' ? low_stock_threshold : '',
    ),
    comment: '',
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

export const errorsSelector = createSelector(
  modalFormSelector,
  ({physical_on_hand, adjustment, low_stock_threshold}) => {
    const errors = {}

    if (!Number(adjustment)) {
      if (!isPresent(physical_on_hand)) {
        errors.physical_on_hand = 'Physical on Hand is required'
      } else if (!isNumeric(physical_on_hand)) {
        errors.physical_on_hand = 'Physical on Hand must be a number'
      }
    }

    if (isPresent(adjustment) && !isNumeric(adjustment)) {
      errors.adjustment = 'Adjustment must be a number'
    }

    if (
      isPresent(low_stock_threshold) &&
      !isPositiveNumeric(low_stock_threshold)
    ) {
      errors.low_stock_threshold =
        'Low Stock Threshold must be a positive number or blank'
    }

    if (Object.keys(errors).length) {
      errors.preventSave = true
    }

    return errors
  },
)

export async function updateSingleProductWarehouse(sku, warehouseID, params) {
  const adjustedParams = structuredClone(params)

  const canEditTotalOnHand = canEditTotalOnHandSelector(getState(), {
    sku,
    warehouseID,
  })

  if (!canEditTotalOnHand) {
    delete adjustedParams.inventory.physical_on_hand
    delete adjustedParams.inventory.adjustment
    delete adjustedParams.inventory.comment
  }

  await saveProductWarehouse(warehouseID, sku, adjustedParams)
}

export async function updateProductWarehouse() {
  try {
    const {
      sku,
      warehouseIDs,
      newWarehouseID,
      location_in_warehouse,
      physical_on_hand,
      adjustment,
      low_stock_threshold,
      comment,
      autoFocusOn,
      isSingleInputEdit,
    } = modalFormSelector(getState())
    const usesInventory = usesInventorySelector(getState())

    updateModalForm({isSaving: true, serverError: null})

    const params = {config: {}, inventory: {}}

    if (!isSingleInputEdit || autoFocusOn === 'location_in_warehouse') {
      params.config.location_in_warehouse = location_in_warehouse
    }

    if (
      usesInventory &&
      (!isSingleInputEdit || autoFocusOn === 'physical_on_hand')
    ) {
      if (Number(adjustment)) {
        params.inventory.adjustment = Number(adjustment)
      } else {
        params.inventory.physical_on_hand = Number(physical_on_hand)
      }

      if (comment) {
        params.inventory.comment = comment
      }
    }

    if (
      usesInventory &&
      (!isSingleInputEdit || autoFocusOn === 'low_stock_threshold')
    ) {
      params.config.low_stock_threshold =
        low_stock_threshold === '' ? null : Number(low_stock_threshold)
    }

    if (newWarehouseID) {
      await updateSingleProductWarehouse(sku, newWarehouseID, params)
    } else {
      await Promise.all(
        warehouseIDs.map((warehouseID) =>
          updateSingleProductWarehouse(sku, warehouseID, params),
        ),
      )
    }

    await getProduct(sku)

    closeModal()

    showMessageToast(
      newWarehouseID
        ? 'Added warehouse location to product'
        : `Saved product warehouse${warehouseIDs.length === 1 ? '' : 's'}`,
    )
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function EditProductWarehouseModal({form}) {
  const firstWarehouseID = form.warehouseIDs[0]
  const errors = useSelector(errorsSelector)
  const firstWarehouse = useSelector((state) =>
    warehouseSelector(state, {warehouseID: firstWarehouseID}),
  )
  const productWarehouses = useSelector((state) =>
    productWarehousesSelector(state, {sku: form.sku}),
  )
  const usesInventory = useSelector(usesInventorySelector)
  const allWarehouseOptions = useSelector(warehouseOptionsSelector)
  const warehouseOptions = useMemo(
    () =>
      allWarehouseOptions.filter(
        ({entity}) => !productWarehouses.find(({id}) => entity.id === id),
      ),
    [allWarehouseOptions],
  )

  return (
    <ConfirmModal
      title={
        form.newWarehouseID ? 'Add Warehouse Location' : 'Edit Warehouse Values'
      }
      modalSize="sm"
      onConfirm={() => updateProductWarehouse()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
    >
      <ul className="list list--no-style">
        {form.newWarehouseID ? (
          <li className="list__item--form list__item--no-style divider--bottom">
            <Zelect
              id={`${MODAL_FORM}__warehouse_select`}
              label="Warehouse"
              value={form.newWarehouseID}
              options={warehouseOptions}
              onChange={(option) =>
                updateModalForm({newWarehouseID: option.entity.id})
              }
            />
          </li>
        ) : (
          <li className="list__item--form list__item--no-style divider--bottom">
            <div className="fs-01">
              {form.warehouseIDs.length === 1 ? (
                <>
                  <strong>Updating 1 warehouse:</strong>
                  <div>{getWarehouseName(firstWarehouse)}</div>
                </>
              ) : (
                <strong>Updating {form.warehouseIDs.length} Warehouses</strong>
              )}
            </div>
          </li>
        )}

        {usesInventory &&
          form.totalThatCanEditTotalOnHand > 0 &&
          (!form.isSingleInputEdit ||
            form.autoFocusOn === 'physical_on_hand') && (
            <>
              <li className="list__item--form list__item--no-style fs-00 margin-bottom-10">
                <strong>Physical On Hand</strong>
                {form.warehouseIDs.length === 1 && (
                  <span>
                    {' '}
                    (
                    {Number(form.adjustment)
                      ? String(
                          Number(form.physical_on_hand) +
                            Number(form.adjustment),
                        )
                      : form.physical_on_hand}{' '}
                    units)
                  </span>
                )}
              </li>
              <li className="list__item--form list__item--no-style flex">
                <div>
                  <label htmlFor="physical-on-hand">Set Total</label>
                  <NumberInput
                    id="physical-on-hand"
                    value={
                      Number(form.adjustment)
                        ? form.warehouseIDs.length === 1
                          ? String(
                              Number(form.physical_on_hand) +
                                Number(form.adjustment),
                            )
                          : ''
                        : form.physical_on_hand
                    }
                    onChange={(value) =>
                      updateModalForm({
                        physical_on_hand: `${value}`,
                      })
                    }
                    isInvalid={!!errors.physical_on_hand}
                    autoFocus={form.autoFocusOn === 'physical_on_hand'}
                    disabled={!!Number(form.adjustment)}
                  />
                  {errors.physical_on_hand && (
                    <small className="error">{errors.physical_on_hand}</small>
                  )}
                </div>
                <div className="flex--justify-col margin-top-20 margin-left-10 margin-right-15">
                  <strong className="text--lt-grey fs-00">OR</strong>
                </div>
                <div>
                  <label htmlFor="physical-on-hand">+/- Units</label>
                  <NumberInput
                    id="adjustment"
                    value={form.adjustment}
                    onChange={(value) =>
                      updateModalForm({
                        adjustment: `${value}`,
                      })
                    }
                    isInvalid={!!errors.adjustment}
                    autoFocus={form.autoFocusOn === 'adjustment'}
                  />
                  {errors.adjustment && (
                    <small className="error">{errors.adjustment}</small>
                  )}
                </div>
              </li>

              <li
                className={classNames('list__item--form list__item--no-style', {
                  'divider--bottom padding-bottom-15':
                    form.warehouseIDs.length === 1,
                })}
              >
                <TextArea
                  label="Reason for Adjustment"
                  id="comment"
                  value={form.comment}
                  onChange={(value) => updateModalForm({comment: value})}
                  errorMessage={errors.comment}
                />
              </li>
            </>
          )}
        {usesInventory &&
          (!form.isSingleInputEdit ||
            form.autoFocusOn === 'low_stock_threshold') && (
            <>
              <li className="list__item--form list__item--no-style fs-00 margin-bottom-3">
                <label className="fs-00" htmlFor="low-stock-threshold">
                  Low Stock Threshold
                </label>
              </li>
              <li
                className={classNames('list__item list__item--form', {
                  'divider--bottom padding-bottom-15':
                    form.warehouseIDs.length === 1,
                })}
              >
                <div className="flex margin-top-15">
                  <NumberInput
                    id="low-stock-threshold"
                    containerClassName="margin-right-10"
                    value={form.low_stock_threshold}
                    onChange={(value) =>
                      updateModalForm({
                        low_stock_threshold: `${value}`,
                      })
                    }
                    isInvalid={!!errors.low_stock_threshold}
                    autoFocus={form.autoFocusOn === 'low_stock_threshold'}
                    min={0}
                  />
                  <div className="flex--justify-col fs-n0 text--lt-grey">
                    <em>Enter a value or leave blank to disable</em>
                  </div>
                </div>
                {errors.low_stock_threshold && (
                  <small className="error">{errors.low_stock_threshold}</small>
                )}
              </li>
            </>
          )}
        {(!form.isSingleInputEdit ||
          form.autoFocusOn === 'location_in_warehouse') && (
          <li className="list__item--form list__item--no-style">
            <TextInput
              label="Location in Warehouse"
              id="location-in-warehouse"
              value={form.location_in_warehouse}
              onChange={(value) =>
                updateModalForm({location_in_warehouse: value})
              }
              errorMessage={errors.location_in_warehouse}
              autoFocus={form.autoFocusOn === 'location_in_warehouse'}
            />
          </li>
        )}
      </ul>
    </ConfirmModal>
  )
}

export default onlyIfForm(EditProductWarehouseModal, modalFormSelector)
